import { Routes } from 'config/constants';

const _READABLE_RESOURCES = [
  'billing',
  'branding',
  'checkout_intents',
  'team',
  'events',
  'webhook_endpoints',
] as const;

const _WRITABLE_RESOURCES = [
  'billing',
  'branding',
  'checkout_intents',
  'team',
  'webhook_endpoints',
] as const;

export type AccessResource = (typeof _READABLE_RESOURCES)[number];
export type WritableAccessResource = (typeof _WRITABLE_RESOURCES)[number];
export type ReadAccessScope = `${AccessResource}:read`;
export type WriteAccessScope = `${WritableAccessResource}:write`;
export type RequiredAccessScope = ReadAccessScope | WriteAccessScope;
export type AccessScope = '*' | RequiredAccessScope;

export type AccessUser = {
  accessScopes?: readonly string[] | null;
  // True when an admin is impersonating this account in read-only mode.
  isReadOnly?: boolean | null;
};

const ROUTE_ACCESS_SCOPES: Partial<Record<Routes, readonly RequiredAccessScope[]>> = {
  [Routes.Dashboard]: ['checkout_intents:write'],
  [Routes.CheckoutIntents]: ['checkout_intents:read'],
  [Routes.Billing]: ['billing:read'],
  [Routes.Payment]: ['billing:write'],
  [Routes.DrawdownSetup]: ['billing:write'],
  [Routes.Team]: ['team:read'],
  [Routes.Commissions]: ['billing:read'],
  [Routes.Commission]: ['billing:read'],
  [Routes.Catalog]: ['checkout_intents:read'],
  [Routes.Account]: ['branding:read', 'checkout_intents:write'],
  [Routes.ApiKeys]: ['checkout_intents:write', 'webhook_endpoints:read'],
};

const DEFAULT_ROUTE_CANDIDATES = [
  Routes.Dashboard,
  Routes.CheckoutIntents,
  Routes.Billing,
  Routes.Account,
  Routes.Team,
  Routes.ApiKeys,
  Routes.Commissions,
  Routes.Catalog,
];

const toReadScope = (resource: AccessResource): ReadAccessScope => `${resource}:read`;

const toWriteScope = (resource: WritableAccessResource): WriteAccessScope => `${resource}:write`;

export const hasScope = (
  accessScopes: readonly string[] | null | undefined,
  scope: RequiredAccessScope,
) => accessScopes?.includes('*') || accessScopes?.includes(scope) || false;

// Transitional: the admin-api does not yet populate accessScopes for every
// account (it currently returns an empty list). Until the backend always
// returns at least one scope - single users get '*', team members get their
// role scopes - treat an empty or absent scope list as legacy full access so
// we don't lock existing users out of the console. Once the backfill ships,
// this fallback becomes dead code and the gating becomes strictly fail-closed.
const hasUnscopedLegacyAccess = (accessScopes: readonly string[] | null | undefined) =>
  !accessScopes || accessScopes.length === 0;

export const canRead = (user: AccessUser | null | undefined, resource: AccessResource) =>
  hasUnscopedLegacyAccess(user?.accessScopes) ||
  hasScope(user?.accessScopes, toReadScope(resource));

export const canWrite = (user: AccessUser | null | undefined, resource: WritableAccessResource) =>
  // Admins impersonating an account in read-only mode can never write, even when
  // the impersonated account's scopes (or legacy unscoped access) would allow it.
  !user?.isReadOnly &&
  (hasUnscopedLegacyAccess(user?.accessScopes) ||
    hasScope(user?.accessScopes, toWriteScope(resource)));

const getRouteAccessScopes = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }

  if (path === `${Routes.CheckoutIntents}/:id?`) {
    return ROUTE_ACCESS_SCOPES[Routes.CheckoutIntents];
  }

  return ROUTE_ACCESS_SCOPES[path as Routes];
};

export const canAccessRoute = (user: AccessUser | null | undefined, path: string | undefined) => {
  const scopes = getRouteAccessScopes(path);

  if (!scopes?.length) {
    return true;
  }

  if (hasUnscopedLegacyAccess(user?.accessScopes)) {
    return true;
  }

  return scopes.some((scope) => hasScope(user?.accessScopes, scope));
};

type DefaultAccessibleRouteOptions = {
  disabledRoutes?: readonly Routes[];
};

export const getDefaultAccessibleRoute = (
  user: AccessUser | null | undefined,
  options: DefaultAccessibleRouteOptions = {},
) => {
  const disabledRoutes = new Set(options.disabledRoutes ?? []);

  return (
    DEFAULT_ROUTE_CANDIDATES.find(
      (route) => !disabledRoutes.has(route) && canAccessRoute(user, route),
    ) ?? Routes.NoAccess
  );
};
